import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useToast } from "../components/Toast";
import { sendComment, reset } from "../features/comments/commentSlice";
import Loader from "../components/Loader";
import {
  HiOutlineMail,
  HiOutlineUser,
  HiOutlineChat,
  HiOutlinePaperAirplane,
  HiOutlineLocationMarker,
  HiOutlinePhone,
  HiOutlineClock,
} from "react-icons/hi";

function Contact() {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    comment: "",
  });

  const dispatch = useDispatch();

  const { name, email, comment } = formData;
  const { isError, isLoading, isSuccess, message } = useSelector((state) => {
    return state.comment;
  });

  useEffect(() => {
    if (isError) {
      toast.error(message || "Transmission failed");
      setFormData({
        name: "",
        email: "",
        comment: "",
      });
    }

    if (isSuccess) {
      toast.success("Message transmitted successfully!");
      setFormData({
        name: "",
        email: "",
        comment: "",
      });
    }

    dispatch(reset());
  }, [isError, message, dispatch, isSuccess, toast]);

  const onChange = (e) => {
    setFormData((prevState) => {
      return {
        ...prevState,
        [e.target.name]: e.target.value,
      };
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    dispatch(sendComment(formData));
  };

  return (
    <div className="min-h-screen bg-[#0b0e14] py-20 px-4 md:px-8 selection:bg-cyan-500/30 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-600/5 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[140px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-[10px] font-black text-cyan-400 uppercase tracking-widest mb-6">
            <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-ping" />
            Communication Protocol v3.2
          </div>
          <h1 className="text-4xl md:text-7xl font-black text-white uppercase italic tracking-tighter mb-4">
            Command <span className="text-cyan-400">Center</span>
          </h1>
          <p className="text-gray-500 font-bold uppercase tracking-[0.2em] text-xs max-w-lg mx-auto">
            Establish direct communication with GamerHub tactical support unit.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-7">
            <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 md:p-12 shadow-2xl relative overflow-hidden group">
              {/* Cyber Accent Line */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500/0 via-cyan-500/50 to-cyan-500/0" />

              <div className="flex items-center gap-4 mb-10">
                <div className="w-14 h-14 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 rounded-2xl flex items-center justify-center font-black shadow-lg shadow-cyan-500/10">
                  <HiOutlineChat className="text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">
                    Transmit Message
                  </h2>
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                    Encrypted Channel Active
                  </p>
                </div>
              </div>

              <form onSubmit={onSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] block ml-1 flex items-center gap-2">
                    <HiOutlineUser className="text-cyan-400" />
                    Agent Identifier
                  </label>
                  <input
                    type="text"
                    placeholder="INPUT_CALLSIGN"
                    name="name"
                    id="name"
                    autoComplete="off"
                    value={name}
                    onChange={onChange}
                    required
                    className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/50 transition-all font-bold text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] block ml-1 flex items-center gap-2">
                    <HiOutlineMail className="text-purple-400" />
                    Comm Frequency
                  </label>
                  <input
                    type="email"
                    placeholder="AGENT_EMAIL_ADDRESS"
                    name="email"
                    id="email"
                    autoComplete="off"
                    value={email}
                    onChange={onChange}
                    required
                    className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500/40 focus:border-purple-500/50 transition-all font-bold text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] block ml-1 flex items-center gap-2">
                    <HiOutlineChat className="text-cyan-400" />
                    Intelligence Report
                  </label>
                  <textarea
                    placeholder="ENTER_TRANSMISSION_DATA..."
                    name="comment"
                    id="comment"
                    autoComplete="off"
                    value={comment}
                    onChange={onChange}
                    required
                    rows={5}
                    className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/50 transition-all font-bold text-sm resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full p-5 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-2xl text-white font-black uppercase tracking-[0.2em] italic text-sm hover:from-cyan-400 hover:to-purple-500 transition-all duration-300 shadow-2xl shadow-cyan-500/20 active:scale-[0.98] disabled:opacity-50 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                  <span className="relative flex justify-center items-center gap-3">
                    {isLoading ? (
                      <Loader size="sm" />
                    ) : (
                      <>
                        <HiOutlinePaperAirplane className="text-xl rotate-90" />
                        Transmit_Signal
                      </>
                    )}
                  </span>
                </button>
              </form>
            </div>
          </div>

          {/* Contact Info Cards */}
          <div className="lg:col-span-5 space-y-6">
            {/* HQ Location */}
            <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2rem] p-8 hover:border-cyan-500/30 transition-all duration-500 group">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-center text-cyan-400 group-hover:scale-110 transition-transform">
                  <HiOutlineLocationMarker className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-white uppercase tracking-widest mb-2 italic">
                    Tactical HQ
                  </h3>
                  <p className="text-gray-400 font-medium text-sm leading-relaxed">
                    1337 Elite Gaming Complex
                    <br />
                    Neo Tokyo District, Sector 7
                    <br />
                    Digital Grid 10001
                  </p>
                </div>
              </div>
            </div>

            {/* Communication Channels */}
            <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2rem] p-8 hover:border-purple-500/30 transition-all duration-500 group">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 bg-purple-500/10 border border-purple-500/20 rounded-xl flex items-center justify-center text-purple-400 group-hover:scale-110 transition-transform">
                  <HiOutlinePhone className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-white uppercase tracking-widest mb-2 italic">
                    Direct Channel
                  </h3>
                  <p className="text-gray-400 font-medium text-sm leading-relaxed">
                    Voice: +1 (555) GAME-HUB
                    <br />
                    Encrypted: support@gamerhub.io
                  </p>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2rem] p-8 hover:border-amber-500/30 transition-all duration-500 group">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
                  <HiOutlineClock className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-white uppercase tracking-widest mb-2 italic">
                    Response Protocol
                  </h3>
                  <p className="text-gray-400 font-medium text-sm leading-relaxed">
                    Priority Response: &lt; 2 hours
                    <br />
                    Standard Queue: 24 hour cycle
                  </p>
                </div>
              </div>
            </div>

            {/* Status Indicator */}
            <div className="bg-gradient-to-br from-green-500/10 to-cyan-500/10 backdrop-blur-2xl border border-green-500/20 rounded-[2rem] p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-500/50" />
                  <span className="text-[10px] font-black text-green-400 uppercase tracking-[0.3em]">
                    All Systems Operational
                  </span>
                </div>
                <span className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">
                  UPTIME: 99.97%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
